import React, { useState, useEffect, useRef } from 'react';
import api from '../api/client';
import './PopupWMProduction.css';

const PopupFertProduction = ({ isOpen, onClose, onSaved, mode, initialData }) => {
  const [recID,        setRecID]        = useState('');
  const [GTIN,         setGTIN]         = useState('');
  const [productName,  setProductName]  = useState('');
  const [SKUForProd,   setSKUForProd]   = useState('');
  const [rawMatID,     setRawMatID]     = useState(null);
  const [bagsPerBox,   setBagsPerBox]   = useState(null);
  const [boxSize,      setBoxSize]      = useState(null);
  const [volumeLb,     setVolumeLb]     = useState(null);
  const [prodDate,     setProdDate]     = useState('');
  const [employee,     setEmployee]     = useState('');
  const [employees,    setEmployees]    = useState([]);

  const [extraEmployees,     setExtraEmployees]     = useState([]);
  const [extraEmployeeSelect,setExtraEmployeeSelect] = useState('');
  const [showExtraEmployees, setShowExtraEmployees]  = useState(false);

  const [isRecordCreated, setIsRecordCreated] = useState(false);

  // Boxes
  const [boxes,            setBoxes]            = useState([]);
  const [scanInput,        setScanInput]        = useState('');
  const [editingBoxIndex,  setEditingBoxIndex]  = useState(null);
  const [editBoxValues,    setEditBoxValues]    = useState({});
  const [boxDuplicateWarning, setBoxDuplicateWarning] = useState('');

  // Bags
  const [bags,           setBags]           = useState([]);
  const [bagScanInput,   setBagScanInput]   = useState('');
  const [bagError,       setBagError]       = useState('');
  const [editingBagIndex,setEditingBagIndex]= useState(null);
  const [editBagValues,  setEditBagValues]  = useState({});
  const [returnModal,    setReturnModal]    = useState(null);

  const [completeProduction, setCompleteProduction] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [gtinError,    setGtinError]    = useState('');
  const [saving,       setSaving]       = useState(false);

  const bagScanRef = useRef(null);
  const recIDRef   = useRef('');
  const SKURef     = useRef('');
  const rawMatRef  = useRef(null);

  useEffect(() => { recIDRef.current = recID; },      [recID]);
  useEffect(() => { SKURef.current   = SKUForProd; }, [SKUForProd]);
  useEffect(() => { rawMatRef.current = rawMatID; },  [rawMatID]);

  function formatDateOnly(value) {
    if (!value) return '';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const d = new Date(value);
    return d.toISOString().split('T')[0];
  }

  function formatBoxDatetime(value) {
    if (!value) return '';
    const s = String(value).replace('T', ' ').replace(/\.\d+Z?$/, '');
    return s.length >= 16 ? s.slice(0, 16) : s;
  }

  useEffect(() => {
    if (!isOpen) return;

    const fetchEmployees = async () => {
      try {
        const r = await api.get(`/api/employees-wm`);
        setEmployees(r.data);
      } catch (err) {
        console.error('Error fetching employees:', err);
      }
    };

    if (mode === 'add') {
      const fetchNextID = async () => {
        try {
          const r = await api.get(`/api/next-prod-fert-id`);
          setRecID(r.data.nextRecId);
        } catch (err) {
          console.error('Error fetching next recID:', err);
        }
      };
      fetchNextID();
      fetchEmployees();
      setGTIN(''); setProductName(''); setSKUForProd(''); setRawMatID(null);
      setBagsPerBox(null); setVolumeLb(null); setBoxSize(null);
      setProdDate(''); setEmployee('');
      setExtraEmployees([]); setExtraEmployeeSelect(''); setShowExtraEmployees(false);
      setBoxes([]); setScanInput('');
      setBags([]); setBagScanInput('');
      setCompleteProduction(false);
      setErrorMessage(''); setGtinError(''); setBoxDuplicateWarning('');
      setEditingBagIndex(null); setEditingBoxIndex(null);
      setIsRecordCreated(false);
    } else if (mode === 'edit' && initialData) {
      fetchEmployees();
      setRecID(initialData.recID || '');
      setIsRecordCreated(true);

      const gtinVal = initialData.GTIN || '';
      setGTIN(gtinVal);
      setProductName(initialData.productName || '');
      setSKUForProd(initialData.SKU || '');
      setRawMatID(initialData.rawMaterialID ?? null);
      setBagsPerBox(initialData.bagsPerBox ?? null);
      setVolumeLb(initialData.volumeLb ?? null);
      setBoxSize(initialData.box_size || null);
      setProdDate(formatDateOnly(initialData.prodDate));
      setEmployee(String(initialData.emplID || ''));

      const allBoxes = (initialData.allBoxes || []).map(box => ({
        boxNumber: box.boxnum || '',
        qty: parseInt(box.qty_per_box, 10) || 0,
        date_added: formatBoxDatetime(box.date_added),
      }));
      setBoxes(allBoxes);

      const loadedBags = (initialData.bagsTaken || []).map(bag => ({
        materialName:   bag.materialName || '',
        bagnum:         bag.bagnum || '',
        rawMaterialID:  bag.rawMaterialID,
        takenWeight:    parseFloat(bag.takenWeight) || 0,
        returnedWeight: parseFloat(bag.returnedWeight) || 0,
      }));
      setBags(loadedBags);

      const loadedExtra = initialData.extraEmployees || [];
      setExtraEmployees(loadedExtra);
      setExtraEmployeeSelect('');
      setShowExtraEmployees(loadedExtra.length > 0);

      setCompleteProduction(initialData.status === 'Complete');
      setErrorMessage(''); setGtinError(''); setBoxDuplicateWarning('');
      setEditingBagIndex(null); setEditingBoxIndex(null);
    }
  }, [mode, initialData, isOpen]);

  const fetchProductByGTIN = async (gtinVal) => {
    if (!gtinVal) return;
    setGtinError(''); setProductName('');
    try {
      const r = await api.get(`/api/fert-product-by-gtin/${encodeURIComponent(gtinVal)}`);
      setProductName(r.data.productName || '');
      setSKUForProd(r.data.SKU || '');
      setRawMatID(r.data.rawMaterialID ?? null);
      setBagsPerBox(r.data.bagsPerBox ?? null);
      setVolumeLb(r.data.volumeLb ?? null);
      setBoxSize(r.data.box_size || null);
    } catch {
      setGtinError('Product not found for this GTIN');
      setProductName(''); setSKUForProd(''); setRawMatID(null);
      setBagsPerBox(null); setVolumeLb(null); setBoxSize(null);
    }
  };

  const ensureRecordExists = async () => {
    if (isRecordCreated) return true;
    if (!SKURef.current) { setErrorMessage('Please enter a valid GTIN first'); return false; }
    if (!prodDate) { setErrorMessage('Production date is required'); return false; }
    if (!employee) { setErrorMessage('Employee is required'); return false; }
    try {
      const r = await api.post(`/api/prod-fert-header`, {
        recID: recIDRef.current || null,
        SKU: SKURef.current,
        emplID: employee,
        prodDate,
        completeProduction: completeProduction ? 1 : 0,
      });
      if (!recIDRef.current) setRecID(r.data.recID);
      setIsRecordCreated(true);
      return true;
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to create production record');
      return false;
    }
  };

  // ── Extra employees ──────────────────────────────────────────────────────────

  const handleAddExtraEmployee = async () => {
    if (!extraEmployeeSelect) return;
    if (extraEmployees.some(e => String(e.employeeID) === String(extraEmployeeSelect))) return;
    if (String(employee) === String(extraEmployeeSelect)) return;
    const ready = await ensureRecordExists();
    if (!ready) return;
    try {
      await api.post(`/api/prod-fert-employee-add`, {
        recID: recIDRef.current,
        employeeID: extraEmployeeSelect,
      });
      const found = employees.find(e => String(e.employeeID) === String(extraEmployeeSelect));
      setExtraEmployees(prev => [...prev, {
        employeeID: extraEmployeeSelect,
        employeeName: found?.employeeName || '',
        addedAt: new Date().toISOString(),
      }]);
      setExtraEmployeeSelect('');
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to add employee');
    }
  };

  const handleRemoveExtraEmployee = async (employeeID) => {
    if (isRecordCreated) {
      try {
        await api.delete(`/api/prod-fert-employee/${encodeURIComponent(recIDRef.current)}/${encodeURIComponent(employeeID)}`);
      } catch (err) {
        setErrorMessage(err.response?.data?.error || 'Failed to remove employee');
        return;
      }
    }
    setExtraEmployees(prev => prev.filter(e => String(e.employeeID) !== String(employeeID)));
  };

  // ── Boxes ────────────────────────────────────────────────────────────────────

  const handleAddBoxes = async () => {
    if (!scanInput.trim()) return;
    setErrorMessage(''); setBoxDuplicateWarning('');
    let parsed;
    try {
      const lines = scanInput.split('\n').filter(l => l.trim());
      parsed = lines.map((line, i) => {
        const parts = line.split(',').map(p => p.trim());
        if (parts.length < 3) throw new Error(`Row ${i + 1}: Invalid format`);
        // Product names may contain commas — SKU and BOX#/QTY are always the last two parts
        const scannedSKU = parts[parts.length - 2];
        if (SKURef.current && scannedSKU !== SKURef.current) {
          throw new Error(`Row ${i + 1}: Wrong product — scanned SKU "${scannedSKU}", expected "${SKURef.current}"`);
        }
        const [rawBoxNum, rawQty] = parts[parts.length - 1].split('/');
        if (!rawBoxNum || !rawQty) throw new Error(`Row ${i + 1}: Invalid BOX/QTY format`);
        return {
          boxNumber: rawBoxNum.replace('BOX#', '').trim(),
          qty: parseInt(rawQty.trim(), 10),
        };
      });
    } catch (err) {
      setErrorMessage(err.message);
      return;
    }

    const ready = await ensureRecordExists();
    if (!ready) return;

    const currentRecID = recIDRef.current;
    const currentSKU   = SKURef.current;
    const addedBoxes   = [];

    for (const box of parsed) {
      try {
        const { data: boxRes } = await api.post(`/api/prod-fert-box-add`, {
          recID: currentRecID,
          boxnum: box.boxNumber,
          sku: currentSKU,
          qty_per_box: box.qty,
          emplID: employee,
        });
        addedBoxes.push({ ...box, date_added: boxRes.date_added || '' });
      } catch (err) {
        if (err.response?.status === 409 && err.response?.data?.existingRecID) {
          setBoxDuplicateWarning(err.response.data.error);
        } else {
          setErrorMessage(err.response?.data?.error || `Failed to add box ${box.boxNumber}`);
        }
        break;
      }
    }

    if (addedBoxes.length > 0) {
      setBoxes(prev => [...prev, ...addedBoxes]);
      setScanInput('');
    }
  };

  const handleRemoveBox = async (index) => {
    const box = boxes[index];
    if (isRecordCreated) {
      try {
        await api.delete(`/api/prod-fert-box/${encodeURIComponent(recIDRef.current)}/${encodeURIComponent(box.boxNumber)}`);
      } catch (err) {
        setErrorMessage(err.response?.data?.error || `Failed to remove box ${box.boxNumber}`);
        return;
      }
    }
    if (editingBoxIndex === index) setEditingBoxIndex(null);
    setBoxes(prev => prev.filter((_, i) => i !== index));
  };

  const handleEditBox   = (index) => { setEditingBoxIndex(index); setEditBoxValues({ newBoxNumber: boxes[index].boxNumber, qty: boxes[index].qty }); };
  const handleCancelBoxEdit = () => { setEditingBoxIndex(null); setEditBoxValues({}); };

  const handleSaveBoxEdit = async (index) => {
    const box = boxes[index];
    const newBoxNumber = editBoxValues.newBoxNumber?.trim() || box.boxNumber;
    const newQty = parseInt(editBoxValues.qty, 10) || box.qty;
    if (isRecordCreated) {
      try {
        await api.put(`/api/prod-fert-box`, {
          recID: recIDRef.current,
          oldBoxnum: box.boxNumber,
          newBoxnum: newBoxNumber,
          qty_per_box: newQty,
        });
      } catch (err) {
        setErrorMessage(err.response?.data?.error || 'Failed to update box');
        return;
      }
    }
    setBoxes(prev => prev.map((b, i) => i === index ? { ...b, boxNumber: newBoxNumber, qty: newQty } : b));
    setEditingBoxIndex(null); setEditBoxValues({});
  };

  // ── Bags ─────────────────────────────────────────────────────────────────────

  const handleBagScanKeyDown = (e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddBag(); } };

  const handleAddBag = async () => {
    const bagnum = bagScanInput.trim().toUpperCase();
    if (!bagnum) return;
    if (!GTIN) { setBagError('Please enter GTIN first'); return; }
    if (bags.some(b => b.bagnum.toUpperCase() === bagnum)) {
      setBagError(`Bag ${bagnum} is already in the list`);
      return;
    }
    setBagError('');

    let bagData;
    try {
      const r = await api.get(`/api/prod-fert-bag-lookup?bagnum=${encodeURIComponent(bagnum)}&sku=${encodeURIComponent(SKURef.current)}`);
      bagData = r.data;
    } catch (err) {
      setBagError(err.response?.data?.error || 'Error looking up bag');
      return;
    }

    const ready = await ensureRecordExists();
    if (!ready) return;

    try {
      const r = await api.post(`/api/prod-fert-bag-add`, {
        recID: recIDRef.current,
        bagnum: bagData.bagnum,
        rawMaterialID: bagData.rawMaterialID,
        emplID: employee,
      });
      setBags(prev => [...prev, {
        materialName:  bagData.materialName || '',
        bagnum:        bagData.bagnum,
        rawMaterialID: bagData.rawMaterialID,
        takenWeight:   (r.data.takenWeight ?? parseFloat(bagData.currentWeight)) || 0,
        returnedWeight: 0,
      }]);
      setBagScanInput('');
      bagScanRef.current?.focus();
    } catch (err) {
      setBagError(err.response?.data?.error || 'Failed to add bag');
    }
  };

  const handleRemoveBag = async (index) => {
    const bag = bags[index];
    if (isRecordCreated) {
      try {
        await api.delete(`/api/prod-fert-bag/${encodeURIComponent(recIDRef.current)}/${encodeURIComponent(bag.bagnum)}`);
      } catch (err) {
        setErrorMessage(err.response?.data?.error || `Failed to remove bag ${bag.bagnum}`);
        return;
      }
    }
    if (editingBagIndex === index) setEditingBagIndex(null);
    setBags(prev => prev.filter((_, i) => i !== index));
  };

  const handleEditBag = (index) => {
    setEditingBagIndex(index);
    setEditBagValues({ takenWeight: bags[index].takenWeight, returnedWeight: bags[index].returnedWeight });
  };
  const handleCancelBagEdit = () => { setEditingBagIndex(null); setEditBagValues({}); };

  const handleSaveBagEdit = async (index) => {
    const bag = bags[index];
    const newReturn = parseFloat(editBagValues.returnedWeight) || 0;
    if (isRecordCreated) {
      try {
        const r = await api.put(`/api/prod-fert-bag-update`, {
          recID: recIDRef.current,
          bagnum: bag.bagnum,
          rawMaterialID: bag.rawMaterialID,
          returnedWeight: newReturn,
        });
        setBags(prev => prev.map((b, i) => i === index ? { ...b, returnedWeight: r.data.netReturn ?? newReturn } : b));
      } catch (err) {
        setErrorMessage(err.response?.data?.error || 'Failed to update bag');
        return;
      }
    } else {
      setBags(prev => prev.map((b, i) => i === index ? { ...b, returnedWeight: newReturn } : b));
    }
    setEditingBagIndex(null); setEditBagValues({});
  };

  const handleReturnSubmit = async () => {
    const { index, weight } = returnModal;
    const bag = bags[index];
    try {
      const r = await api.put(`/api/prod-fert-bag-update`, {
        recID: recIDRef.current,
        bagnum: bag.bagnum,
        rawMaterialID: bag.rawMaterialID,
        returnedWeight: parseFloat(weight) || 0,
      });
      const netReturn = r.data.netReturn ?? parseFloat(weight) ?? 0;
      setBags(prev => prev.map((b, i) => i === index ? { ...b, returnedWeight: netReturn } : b));
      setReturnModal(null);
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to save return weight');
    }
  };

  // ── Totals ───────────────────────────────────────────────────────────────────

  const totalBoxesQty   = boxes.reduce((s, b) => s + (b.qty || 0), 0);
  const totalTakenLb    = bags.reduce((s, b) => s + (parseFloat(b.takenWeight) || 0), 0);
  const requiredLb      = volumeLb != null ? totalBoxesQty * parseFloat(volumeLb) : null;
  const allowedMinLb    = requiredLb != null ? requiredLb * 0.9 : null;
  const deficitLb       = allowedMinLb != null && boxes.length > 0 && totalTakenLb < allowedMinLb
    ? allowedMinLb - totalTakenLb : 0;

  // ── Save header ──────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!GTIN)        { setErrorMessage('GTIN is required'); return; }
    if (!SKUForProd)  { setErrorMessage('Product not found for this GTIN'); return; }
    if (!prodDate)    { setErrorMessage('Production date is required'); return; }
    if (!employee)    { setErrorMessage('Employee is required'); return; }
    setErrorMessage(''); setSaving(true);
    try {
      await api.post(`/api/prod-fert-header`, {
        recID: recIDRef.current || null,
        SKU: SKUForProd,
        emplID: employee,
        prodDate,
        completeProduction: completeProduction ? 1 : 0,
      });
      setIsRecordCreated(true);
      onSaved?.();
      onClose();
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to save production record');
    } finally {
      setSaving(false);
    }
  };

  const availableForExtra = employees.filter(e =>
    String(e.employeeID) !== String(employee) &&
    !extraEmployees.some(ex => String(ex.employeeID) === String(e.employeeID))
  );

  if (!isOpen) return null;

  return (
    <div className="popup open">
      <div className="popup__body popup__body--wide">
        <div className="popup__content popup__content--wide">
          <button className="popup__close-button" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <use xlinkHref="#closeBtn"></use>
            </svg>
          </button>

          <h2 className="popup__title">Production Fertilizers</h2>

          {/* ── Header ─────────────────────────────────────── */}
          <div className="prod-header-row">
            <div className="prod-header-field">
              <label className="label">Record ID</label>
              <input type="text" className="form__input" value={recID} readOnly />
            </div>

            <div className="prod-header-field">
              <label className="label">GTIN</label>
              <input
                type="text"
                className="form__input"
                value={GTIN}
                onChange={e => { setGTIN(e.target.value); setProductName(''); setGtinError(''); }}
                onBlur={() => fetchProductByGTIN(GTIN)}
                onKeyDown={e => { if (e.key === 'Enter') fetchProductByGTIN(GTIN); }}
                placeholder="Scan or enter GTIN"
              />
              {gtinError && <span className="field-error">{gtinError}</span>}
              {productName && (
                <div className="gtin-product-info">
                  <span className="gtin-product-name">{productName}</span>
                  {bagsPerBox != null && (
                    <span className="gtin-packs-per-box">{bagsPerBox} packs / box</span>
                  )}
                  {boxSize && <span className="gtin-box-size" title="Box size, inches"><i className="fa-solid fa-box"></i>{boxSize}</span>}
                </div>
              )}
            </div>

            <div className="prod-header-field">
              <label className="label">Production Date</label>
              <input type="date" className="form__input" value={prodDate} onChange={e => setProdDate(e.target.value)} />
            </div>

            <div className="prod-header-field">
              <label className="label">Employee</label>
              <select className="slct" value={employee} onChange={e => setEmployee(e.target.value)}>
                <option value="">Select Employee</option>
                {employees.map(emp => (
                  <option key={emp.employeeID} value={emp.employeeID}>{emp.fullName || emp.employeeName}</option>
                ))}
              </select>
              <button className="prod-extra-employees-toggle" onClick={() => setShowExtraEmployees(v => !v)}>
                {showExtraEmployees ? '− Hide additional employees' : '+ Add more employees'}
                {!showExtraEmployees && extraEmployees.length > 0 && (
                  <span className="prod-extra-employees-count">{extraEmployees.length}</span>
                )}
              </button>
            </div>
          </div>

          {/* ── Extra employees ─────────────────────────────── */}
          {showExtraEmployees && (
            <div className="prod-extra-employees-row">
              <span className="label prod-extra-employees-label">Additional employees:</span>
              <div className="prod-extra-employees-add">
                <select className="slct prod-extra-employees-select" value={extraEmployeeSelect} onChange={e => setExtraEmployeeSelect(e.target.value)}>
                  <option value="">Select employee…</option>
                  {availableForExtra.map(emp => (
                    <option key={emp.employeeID} value={emp.employeeID}>{emp.fullName || emp.employeeName}</option>
                  ))}
                </select>
                <button className="btn btn__white_g prod-add-btn" onClick={handleAddExtraEmployee}>+ Add</button>
              </div>
              {extraEmployees.length > 0 && (
                <div className="prod-extra-employees-list">
                  {extraEmployees.map(emp => (
                    <span key={emp.employeeID} className="prod-extra-employee-tag">
                      {emp.employeeName || `#${emp.employeeID}`}
                      <button className="prod-extra-employee-remove" onClick={() => handleRemoveExtraEmployee(emp.employeeID)}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Two-column section ──────────────────────────── */}
          <div className="prod-columns-row">

            {/* LEFT: Boxes produced */}
            <div className="prod-column">
              <div className="prod-column-header">
                <span className="prod-column-title">Boxes Produced</span>
              </div>
              <div className="prod-scan-area">
                <textarea
                  className="scannerform__textarea"
                  rows="4"
                  placeholder="Scan boxes here (one per line)"
                  value={scanInput}
                  onChange={e => setScanInput(e.target.value)}
                />
                <button className="btn btn__white_g prod-add-btn" onClick={handleAddBoxes}>ADD</button>
              </div>
              {boxDuplicateWarning && <div className="field-error">{boxDuplicateWarning}</div>}
              {boxes.length > 0 && (
                <table className="prod-table">
                  <thead>
                    <tr>
                      <th>Box #</th>
                      <th>QTY</th>
                      <th>Added</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {boxes.map((box, i) => (
                      <tr key={i}>
                        {editingBoxIndex === i ? (
                          <>
                            <td><input className="prod-inline-input" value={editBoxValues.newBoxNumber} onChange={e => setEditBoxValues(v => ({ ...v, newBoxNumber: e.target.value }))} /></td>
                            <td><input className="prod-inline-input prod-inline-input--sm" type="number" value={editBoxValues.qty} onChange={e => setEditBoxValues(v => ({ ...v, qty: e.target.value }))} /></td>
                            <td>{box.date_added}</td>
                            <td className="col-actions">
                              <div className="prod-table__actions">
                                <button className="prod-table__icon-btn prod-table__icon-btn--confirm" title="Save" onClick={() => handleSaveBoxEdit(i)}><i className="fa fa-check"></i></button>
                                <button className="prod-table__icon-btn" title="Cancel" onClick={handleCancelBoxEdit}><i className="fa fa-times"></i></button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>{box.boxNumber}</td>
                            <td>{box.qty}</td>
                            <td>{box.date_added}</td>
                            <td className="col-actions">
                              <div className="prod-table__actions">
                                <button className="prod-table__icon-btn" title="Edit" onClick={() => handleEditBox(i)}><i className="fa fa-pencil"></i></button>
                                <button className="prod-table__icon-btn prod-table__icon-btn--danger" title="Remove" onClick={() => handleRemoveBox(i)}><i className="fa fa-trash"></i></button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="2"><strong>{boxes.length} boxes · {totalBoxesQty} packs</strong></td>
                      <td colSpan="2" />
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>

            {/* RIGHT: Bags used */}
            <div className="prod-column">
              <div className="prod-column-header">
                <span className="prod-column-title">Bags Used</span>
                {totalTakenLb > 0 && (
                  <span className="prod-column-subtitle">
                    Taken: {totalTakenLb.toFixed(2)} lb
                    {requiredLb != null && ` · Required: ${requiredLb.toFixed(2)} lb`}
                  </span>
                )}
              </div>
              <div className="prod-scan-area prod-scan-area--row">
                <input
                  ref={bagScanRef}
                  type="text"
                  className="form__input"
                  value={bagScanInput}
                  onChange={e => { setBagScanInput(e.target.value); setBagError(''); }}
                  onKeyDown={handleBagScanKeyDown}
                  placeholder="Scan bag number, press Enter"
                />
                <button className="btn btn__white_g prod-add-btn" onClick={handleAddBag}>ADD</button>
              </div>
              {bagError && <div className="field-error">{bagError}</div>}
              {bags.length > 0 && (
                <table className="prod-table">
                  <thead>
                    <tr>
                      <th>Bag #</th>
                      <th>Material</th>
                      <th>Taken lb</th>
                      <th>Returned lb</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {bags.map((bag, i) => (
                      <tr key={i}>
                        {editingBagIndex === i ? (
                          <>
                            <td>{bag.bagnum}</td>
                            <td>{bag.materialName}</td>
                            <td><input className="prod-inline-input prod-inline-input--sm" type="number" step="0.01" value={editBagValues.takenWeight} onChange={e => setEditBagValues(v => ({ ...v, takenWeight: e.target.value }))} /></td>
                            <td><input className="prod-inline-input prod-inline-input--sm" type="number" step="0.01" value={editBagValues.returnedWeight} onChange={e => setEditBagValues(v => ({ ...v, returnedWeight: e.target.value }))} /></td>
                            <td className="col-actions">
                              <div className="prod-table__actions">
                                <button className="prod-table__icon-btn prod-table__icon-btn--confirm" title="Save" onClick={() => handleSaveBagEdit(i)}><i className="fa fa-check"></i></button>
                                <button className="prod-table__icon-btn" title="Cancel" onClick={handleCancelBagEdit}><i className="fa fa-times"></i></button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>{bag.bagnum}</td>
                            <td>{bag.materialName}</td>
                            <td>{bag.takenWeight}</td>
                            <td>
                              {bag.returnedWeight > 0
                                ? bag.returnedWeight
                                : <button className="prod-table__icon-btn prod-return-btn" title="Return weight" onClick={() => setReturnModal({ index: i, bagnum: bag.bagnum, weight: '' })}>Return</button>
                              }
                            </td>
                            <td className="col-actions">
                              <div className="prod-table__actions">
                                <button className="prod-table__icon-btn" title="Edit" onClick={() => handleEditBag(i)}><i className="fa fa-pencil"></i></button>
                                <button className="prod-table__icon-btn prod-table__icon-btn--danger" title="Remove" onClick={() => handleRemoveBag(i)}><i className="fa fa-trash"></i></button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

          </div>

          {/* ── Weight warning ──────────────────────────────── */}
          {requiredLb != null && boxes.length > 0 && deficitLb > 0 && (
            <div className="prod-weight-warning">
              <i className="fa fa-exclamation-triangle"></i>
              <span>
                Material weight insufficient.&nbsp;
                <strong>Required: {requiredLb.toFixed(2)} lb</strong>&nbsp;·&nbsp;
                <strong>Taken: {totalTakenLb.toFixed(2)} lb</strong>&nbsp;·&nbsp;
                <strong className="prod-weight-warning__deficit">Missing: {deficitLb.toFixed(2)} lb</strong>
              </span>
            </div>
          )}

          {/* ── Complete checkbox + save ─────────────────────── */}
          <div className="prod-footer-row">
            <label className="prod-complete-label">
              <input type="checkbox" checked={completeProduction} disabled={deficitLb > 0} onChange={e => setCompleteProduction(e.target.checked)} />
              <span>Mark as Complete</span>
            </label>
            {errorMessage && <div className="popup__error-message">{errorMessage}</div>}
            <button className="btn btn__green" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>

        </div>
      </div>

      {/* ── Return weight modal ──────────────────────────────── */}
      {returnModal && (
        <div className="popup open" style={{ zIndex: 2000 }}>
          <div className="popup__body">
            <div className="popup__content">
              <button className="popup__close-button" onClick={() => setReturnModal(null)}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><use xlinkHref="#closeBtn" /></svg>
              </button>
              <h3 className="popup__title">Return Weight — Bag {returnModal.bagnum}</h3>
              <div className="prod-header-field" style={{ marginTop: 16 }}>
                <label className="label">Returned weight (lb)</label>
                <input
                  type="number" step="0.01" min="0"
                  className="form__input"
                  value={returnModal.weight}
                  onChange={e => setReturnModal(v => ({ ...v, weight: e.target.value }))}
                  autoFocus
                />
              </div>
              <div className="prod-footer-row" style={{ marginTop: 16 }}>
                <button className="btn btn__white_g" onClick={() => setReturnModal(null)}>Cancel</button>
                <button className="btn btn__green" onClick={handleReturnSubmit}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PopupFertProduction;
